import { View, type ViewStyle } from "react-native";
import { ChevronRight } from "lucide-react-native";
import { colors, typography } from "../theme/tokens";
import { MoonText } from "./MoonText";
import { MoonPressable } from "./MoonPressable";

/**
 * Settings-style row: optional left icon, label, value / control on the
 * right, optional chevron. Audit task 2.5.
 *
 * Matches the FertilityProfile / AccountSettings / NotificationsSettings
 * row shape (16px horizontal, 14px vertical, optional top border for
 * separation when stacked). Replaces the moon-app-local `CardRow` from
 * `YouScreen/components.tsx`.
 *
 * Trailing precedence: `trailing` > `value` > nothing. The chevron auto-
 * renders when `onPress` is set unless `chevron={false}` is passed.
 *
 * State coverage (Phase 1.0 matrix):
 *   - idle / pressed  — MoonPressable handles haptic + scale animation
 *     when `onPress` is set; static `View` otherwise
 *   - disabled        — opacity 0.5, presses ignored
 *   - error / focused / loading — N/A; rows are navigational shells.
 */
type Props = {
  label: string;
  /** Leading icon (left of the label). */
  icon?: React.ReactNode;
  /** Right-aligned value text. Pre-empted by `trailing` when both passed. */
  value?: string;
  /** Color override for the value text. Defaults to `colors.textLight`. */
  valueColor?: string;
  /** Custom right-side element (toggle, badge, etc.). */
  trailing?: React.ReactNode;
  onPress?: () => void;
  /** Show chevron on the right. Defaults to true when onPress is set. */
  chevron?: boolean;
  /** Top border for stacking. Defaults to false (first row). */
  divider?: boolean;
  disabled?: boolean;
  testID?: string;
  style?: ViewStyle;
};

export function MoonSettingRow({
  label,
  icon,
  value,
  valueColor,
  trailing,
  onPress,
  chevron,
  divider,
  disabled,
  testID,
  style,
}: Props) {
  const showChevron = chevron ?? !!onPress;
  const rowStyle: ViewStyle = {
    flexDirection: "row",
    alignItems: "center",
    gap: icon ? 14 : 0,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderTopWidth: divider ? 1 : 0,
    borderTopColor: colors.border,
  };

  const inner = (
    <>
      {icon}
      <MoonText
        style={{
          flex: 1,
          fontFamily: typography.families.sansMedium,
          fontSize: 15,
          fontWeight: "500",
          color: colors.text,
        }}
      >
        {label}
      </MoonText>
      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        {trailing ??
          (value ? (
            <MoonText
              style={{
                fontFamily: typography.families.sans,
                fontSize: 14,
                color: valueColor ?? colors.textLight,
              }}
            >
              {value}
            </MoonText>
          ) : null)}
        {showChevron ? (
          <ChevronRight size={16} color={colors.textFaint} />
        ) : null}
      </View>
    </>
  );

  if (onPress) {
    return (
      <MoonPressable
        accessibilityRole="button"
        accessibilityState={{ disabled }}
        onPress={onPress}
        disabled={disabled}
        haptic="light"
        activeScale={0.98}
        activeOpacity={0.7}
        testID={testID}
        style={[rowStyle, style]}
      >
        {inner}
      </MoonPressable>
    );
  }

  return (
    <View testID={testID} style={[rowStyle, style]}>
      {inner}
    </View>
  );
}
